import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * MemoApprovalModal - Quick approve/reject memo modal
 * Uses gold theme from BaseModal system
 */
import { useState } from 'react';
import styled from 'styled-components';
import BaseModal, { Textarea as BaseTextarea, FormGroup as BaseFormGroup, FormLabel, PrimaryButton, SecondaryButton, DangerButton, GhostButton, } from './shared/BaseModal';
import mockDataService from '../services/mockDataService';
import { notify } from '../utils/notifications';
import apiClient from '../utils/apiClient';
import { MemoStatus } from '../types/memo';
const InfoBox = styled.div `
  background: var(--bg-tertiary);
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid var(--border-color);
`;
const DiamondPreview = styled.div `
  text-align: center;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);

  h3 {
    margin: 0 0 8px 0;
    color: var(--text-primary);
    font-family: 'Playfair Display', serif;
    font-size: 20px;
  }

  .specs {
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.6;
  }

  .price {
    margin-top: 16px;
    font-size: 28px;
    font-weight: 600;
    color: var(--accent-gold);
    font-family: 'Playfair Display', serif;
  }
`;
const InfoRow = styled.div `
  display: flex;
  justify-content: space-between;
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border-color);
  }
`;
const InfoLabel = styled.span `
  font-size: 14px;
  color: var(--text-tertiary);
`;
const InfoValue = styled.span `
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 600;
`;
const TextArea = styled(BaseTextarea) `
  min-height: 80px;
`;
const FormGroup = styled(BaseFormGroup) `
  margin-bottom: 16px;
`;
export const MemoApprovalModal = ({ isOpen, onClose, memo, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [action, setAction] = useState(null);
    const [adminNotes, setAdminNotes] = useState('');
    const handleAction = async (actionType) => {
        if (!memo)
            return;
        setLoading(true);
        try {
            const newStatus = actionType === 'approve' ? MemoStatus.APPROVED : MemoStatus.REJECTED;
            if (mockDataService.isMockMode()) {
                // Update mock memo
                mockDataService.updateMemo(memo.id, {
                    status: newStatus,
                    approvedAt: actionType === 'approve' ? new Date().toISOString() : undefined,
                    adminNotes
                });
                notify.success(actionType === 'approve' ? 'Memo Approved' : 'Memo Rejected', `Memo for ${memo.dealerName} has been ${actionType === 'approve' ? 'approved' : 'rejected'}`);
                onSuccess?.();
                onClose();
            }
            else {
                // Real API
                const response = await apiClient.put(`memos/update-status/${memo.id}`, {
                    status: newStatus,
                    adminNotes
                });
                if (response.ok && response.data?.success) {
                    notify.success(actionType === 'approve' ? 'Memo Approved' : 'Memo Rejected', `Memo has been ${actionType === 'approve' ? 'approved' : 'rejected'}`);
                    onSuccess?.();
                    onClose();
                }
                else {
                    notify.error('Failed', response.error || 'Failed to update memo');
                }
            }
        }
        catch (error) {
            console.error('Memo action failed:', error);
            notify.error('Error', 'Failed to update memo');
        }
        finally {
            setLoading(false);
            setAction(null);
        }
    };
    if (!memo)
        return null;
    const getFooter = () => {
        if (action === 'approve') {
            return (_jsxs(_Fragment, { children: [_jsx(GhostButton, { onClick: () => setAction(null), children: "Back" }), _jsx(PrimaryButton, { onClick: () => handleAction('approve'), disabled: loading, children: loading ? 'Approving...' : 'Confirm Approval' })] }));
        }
        if (action === 'reject') {
            return (_jsxs(_Fragment, { children: [_jsx(GhostButton, { onClick: () => setAction(null), children: "Back" }), _jsx(DangerButton, { onClick: () => handleAction('reject'), disabled: loading, children: loading ? 'Rejecting...' : 'Confirm Rejection' })] }));
        }
        return (_jsxs(_Fragment, { children: [_jsx(GhostButton, { onClick: onClose, children: "Close" }), _jsx(SecondaryButton, { onClick: () => setAction('reject'), children: "Reject" }), _jsx(PrimaryButton, { onClick: () => setAction('approve'), children: "Approve" })] }));
    };
    return (_jsxs(BaseModal, { isOpen: isOpen, onClose: onClose, title: action ? `Confirm ${action === 'approve' ? 'Approval' : 'Rejection'}` : 'Review Memo Request', titleAccent: action ? (action === 'approve' ? 'Approval' : 'Rejection') : 'Memo', size: "md", footer: getFooter(), children: [_jsxs(DiamondPreview, { children: [_jsxs("h3", { children: [memo.diamondDetails.caratWeight, "ct ", memo.diamondDetails.shape] }), _jsxs("div", { className: "specs", children: ["Color: ", memo.diamondDetails.color, " \u2022 Clarity: ", memo.diamondDetails.clarity, " \u2022 Cut: ", memo.diamondDetails.cut, _jsx("br", {}), "Lab: ", memo.diamondDetails.lab, memo.diamondDetails.certificateNumber && ` • Cert #${memo.diamondDetails.certificateNumber}`, memo.diamondDetails.stockNumber && _jsx("br", {}), memo.diamondDetails.stockNumber && `Stock #${memo.diamondDetails.stockNumber}`] }), _jsxs("div", { className: "price", children: ["$", memo.diamondDetails.priceUSD.toLocaleString()] })] }), _jsxs(InfoBox, { children: [_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Dealer:" }), _jsx(InfoValue, { children: memo.dealerName })] }), memo.dealerBusinessName && (_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Business:" }), _jsx(InfoValue, { children: memo.dealerBusinessName })] })), memo.clientName && (_jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Client:" }), _jsx(InfoValue, { children: memo.clientName })] })), _jsxs(InfoRow, { children: [_jsx(InfoLabel, { children: "Requested:" }), _jsx(InfoValue, { children: new Date(memo.requestedAt).toLocaleDateString() })] })] }), memo.dealerNotes && (_jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Dealer Notes:" }), _jsxs("div", { style: {
                            padding: '12px',
                            background: 'var(--bg-tertiary)',
                            borderRadius: '6px',
                            fontSize: '14px',
                            color: 'var(--text-secondary)',
                            fontStyle: 'italic',
                            borderLeft: '3px solid var(--accent-gold)'
                        }, children: ["\"", memo.dealerNotes, "\""] })] })), action && (_jsxs(FormGroup, { children: [_jsx(FormLabel, { children: "Admin Notes (Optional):" }), _jsx(TextArea, { value: adminNotes, onChange: (e) => setAdminNotes(e.target.value), placeholder: action === 'approve'
                            ? 'Add approval notes (optional)...'
                            : 'Add rejection reason (optional)...' })] }))] }));
};
